import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, format, isAfter, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TimeSlot, Service, SalonSettings, TeamMember, DEFAULT_SALON_SETTINGS } from '../../types';
import { generateTimeSlots, isWorkingDay } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

interface DateTimeSelectionProps {
  service: Service;
  appointments: any[];
  blockedTimes: any[];
  selectedDate: Date | null;
  selectedTimeSlot: TimeSlot | null;
  onSelectDate: (date: Date) => void;
  onSelectTimeSlot: (timeSlot: TimeSlot) => void;
  loading?: boolean;
  salonSettings?: SalonSettings;
  teamMember?: TeamMember | null;
}

const DateTimeSelection: React.FC<DateTimeSelectionProps> = ({
  service,
  appointments,
  blockedTimes,
  selectedDate,
  selectedTimeSlot,
  onSelectDate,
  onSelectTimeSlot,
  loading = false,
  salonSettings = DEFAULT_SALON_SETTINGS,
  teamMember,
}) => {
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const today = startOfDay(new Date());
  const minDate = today;
  const maxDate = addDays(today, 60);

  useEffect(() => {
    if (!selectedDate || !service) {
      setAvailableTimeSlots([]);
      return;
    }

    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const slots = generateTimeSlots(formattedDate, service, appointments, blockedTimes, salonSettings, teamMember);
      setAvailableTimeSlots(slots);
      setError(null);
    } catch (err) {
      setError('Failed to load available time slots. Please try again.');
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, service, appointments, blockedTimes, salonSettings, teamMember]);

  const groupedTimeSlots = availableTimeSlots.reduce(
    (acc, slot) => {
      const hour = parseInt(slot.startTime.split(':')[0], 10);
      if (hour < 12) {
        acc.morning.push(slot);
      } else if (hour < 17) {
        acc.afternoon.push(slot);
      } else {
        acc.evening.push(slot);
      }
      return acc;
    },
    { morning: [] as TimeSlot[], afternoon: [] as TimeSlot[], evening: [] as TimeSlot[] }
  );

  const renderTimeSlots = (slots: TimeSlot[], title: string) => {
    if (slots.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 1 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {slots.map((slot, index) => {
            const isSelected = selectedTimeSlot?.startTime === slot.startTime;
            return (
              <Button
                key={index}
                variant={isSelected ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => onSelectTimeSlot(slot)}
                disabled={!slot.available}
                sx={{
                  minWidth: 90,
                  borderRadius: 2,
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: '0.85rem',
                  py: 0.8,
                  px: 1.5,
                  boxShadow: isSelected ? 2 : 0,
                  textTransform: 'none',
                }}
              >
                {format(parse(slot.startTime, 'HH:mm', new Date()), 'h:mm a')}
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  };

  const parse = (time: string, formatStr: string, baseDate: Date) => {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date(baseDate);
    date.setHours(hours, minutes);
    return date;
  };

  const shouldDisableDate = (date: Date) => {
    if (isBefore(date, today)) return true;
    if (isAfter(date, maxDate)) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    return !isWorkingDay(dateStr, salonSettings, teamMember);
  };

  // Get the date-fns locale based on current language
  const getLocale = () => {
    if (i18n.language === 'fr') return fr;
    return undefined;
  };

  const formatDateLabel = (date: Date) => {
    const locale = getLocale();
    if (locale) {
      return format(date, 'EEEE d MMMM yyyy', { locale });
    }
    return format(date, 'EEEE, MMMM d, yyyy');
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        {t('dateTime.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('dateTime.subtitle')}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Calendar */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Paper sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => {
                  if (newDate) onSelectDate(newDate);
                }}
                minDate={minDate}
                maxDate={maxDate}
                shouldDisableDate={shouldDisableDate}
                disablePast
              />
            </LocalizationProvider>
          </Paper>
        </Box>

        {/* Time Slots */}
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : selectedDate ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('dateTime.availableFor')} {formatDateLabel(selectedDate)}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {!error && availableTimeSlots.length === 0 && (
                  <Alert severity="info">
                    {t('dateTime.noSlots')}
                  </Alert>
                )}

                {renderTimeSlots(groupedTimeSlots.morning, t('dateTime.morning'))}
                {renderTimeSlots(groupedTimeSlots.afternoon, t('dateTime.afternoon'))}
                {renderTimeSlots(groupedTimeSlots.evening, t('dateTime.evening'))}
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('dateTime.selectDate')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default DateTimeSelection;
